// WHICH PATCHES ARE NEWS. `/v1/streams` re-sends a topic's whole snapshot as
// upserts on every connection — a ~10 min recycle, a dropped socket, and every
// route change that alters the shared stream's topic set — because the server's
// diff state is per CONNECTION. Read as changes, each replay re-read every live
// read on the page at once. This remembers the `version` this tab last saw per
// `(topic, key)`, ACROSS connections, so a replayed row at the same version says
// nothing and only a row that actually moved is a change.
//
// BOUNDED BY TWO GENERATIONS PER TOPIC, not by hope. A connection's `ready`
// rotates each topic it serves: the current generation becomes the previous one
// and a fresh one starts, and a version is looked up in current, then previous.
// So a key the server stopped sending (a row that left the head during a gap,
// whose `deleted` no connection ever saw) is forgotten one connection later, and
// the memory never holds more than two snapshots' worth of keys per topic.
//
// What it does NOT decide: a `deleted` patch is always news (the row left), and a
// first sighting is always news (nothing says the read saw it). The first
// connection of a tab therefore still signals its snapshot once — there is no
// earlier generation to compare against.

import type { StreamPatch } from '@developerz/domain';

type Generations = { current: Map<string, string>; previous: Map<string, string> };

export type VersionMemory = {
  /** Whether `patch` is a change to its `(topic, key)`, recording its version either way. */
  readonly isNews: (patch: StreamPatch) => boolean;
  /**
   * A connection is serving `topics` from scratch: rotate their generations.
   * Answers the topics the PREVIOUS connection held rows for — the ones a row
   * may have vanished from with no patch to say so.
   */
  readonly rotate: (topics: readonly string[]) => string[];
  /** Forget everything — another account's rows are not this one's history. */
  readonly clear: () => void;
};

export function createVersionMemory(): VersionMemory {
  const topics = new Map<string, Generations>();
  const of = (topic: string): Generations => {
    let g = topics.get(topic);
    if (g === undefined) {
      g = { current: new Map(), previous: new Map() };
      topics.set(topic, g);
    }
    return g;
  };
  return {
    isNews: (patch) => {
      const g = of(patch.topic);
      if ('deleted' in patch) {
        g.current.delete(patch.key);
        g.previous.delete(patch.key);
        return true;
      }
      const seen = g.current.get(patch.key) ?? g.previous.get(patch.key);
      g.current.set(patch.key, patch.version);
      return seen !== patch.version;
    },
    rotate: (served) => {
      const held: string[] = [];
      for (const topic of served) {
        const g = of(topic);
        if (g.current.size > 0) held.push(topic);
        g.previous = g.current;
        g.current = new Map();
      }
      return held;
    },
    clear: () => topics.clear(),
  };
}
